import {useLayoutEffect} from 'react';
import {useLocation} from 'react-router-dom';
import {Role} from '@/config/roles';
import {ROUTES} from '@/config/routesConstants';
import {MENU_ITEMS__FIFA_REVAMP} from '@/components/menu/menuItems';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import useNavigation from '@/hooks/use-navigation';

/**
 * Routes that are allowed when fifaRevamp flag is enabled.
 * Users accessing any other route will be redirected to /creator-crm.
 * Includes all routes from the FIFA revamp menu plus supporting routes.
 */
const ALLOWED_ROUTES_FIFA_REVAMP = [
  // FIFA revamp menu routes - appearing in menu (brand/admin)
  ROUTES.HOME,
  ROUTES.CREATOR_CRM,
  ROUTES.RECRUIT_CRM,
  ROUTES.MY_CREATORS,
  ROUTES.CAMPAIGNS, // Includes campaign detail routes (/campaigns/:id/*)
  ROUTES.SEARCH,
  ROUTES.THREADS,
  // Legacy routes - retained for CSMs (platform_admin)
  ROUTES.STATS,
  ROUTES.CONTENT__LIBRARY,
  ROUTES.PARTNERSHIPS,
  // Supporting routes - not appearing in menu but allowed
  ROUTES.VETTING,
  ROUTES.NEW_CAMPAIGNS,
  ROUTES.CREATOR_PROFILE,
  ROUTES.PREFERENCES,
  ROUTES.ORGANIZATION_SETTINGS,
  ROUTES.LOGIN,
  ROUTES.SIGN_UP,
  ROUTES.SIGN_UP_REACT_OLD,
  ROUTES.PUBLIC_CREATOR_PROFILE, // /p
  ROUTES.SHARED_LIST,
  ROUTES.VIDEO_PREVIEWS,
  ROUTES.PHOTO_PREVIEWS,
  '/errors', // ROUTES.ERROR contains regex, using simple path for startsWith matching
];

/**
 * Checks if a route matches a menu item's link path.
 * Handles exact matches and nested routes (e.g., /creator-profile/123).
 */
function matchesMenuItemLink(pathname: string, menuItemLink: string): boolean {
  // Skip menu items with empty links (e.g., separators)
  if (!menuItemLink) {
    return false;
  }
  if (menuItemLink === '/') {
    return pathname === '/';
  }
  return pathname.startsWith(menuItemLink);
}

/**
 * Checks if the user has any of the required roles for a menu item.
 * If no roles are required, the route is accessible to all users.
 */
function hasRequiredRoles(
  requiredRoles: Role[],
  userRoles: Array<{name: string}> | undefined
): boolean {
  if (requiredRoles.length === 0) {
    return true;
  }

  if (!userRoles || userRoles.length === 0) {
    return false;
  }

  const userRoleNames = userRoles.map((role) => role.name);
  return requiredRoles.some((requiredRole) =>
    userRoleNames.includes(requiredRole)
  );
}

/**
 * Checks if the current pathname matches any of the allowed routes.
 * First checks MENU_ITEMS__FIFA_REVAMP with role validation,
 * then falls back to ALLOWED_ROUTES_FIFA_REVAMP for supporting routes.
 */
function isRouteAllowed(
  pathname: string,
  userRoles: Array<{name: string}> | undefined
): boolean {
  const matchingMenuItem = MENU_ITEMS__FIFA_REVAMP.find((menuItem) =>
    matchesMenuItemLink(pathname, menuItem.link)
  );

  if (matchingMenuItem) {
    return hasRequiredRoles(matchingMenuItem.requiredRoles, userRoles);
  }

  return ALLOWED_ROUTES_FIFA_REVAMP.some((allowedRoute) => {
    if (allowedRoute === '/') {
      return pathname === '/';
    }
    return pathname.startsWith(allowedRoute);
  });
}

/**
 * Hook that redirects users to /creator-crm when fifaRevamp flag is enabled
 * and they try to access a route that is not in the allowed list.
 */
function useFifaRevampRedirect(isFifaRevampEnabled: boolean): void {
  const location = useLocation();
  const {navigate} = useNavigation();
  const {dashboardUser} = useDashboardUserQuery({});

  useLayoutEffect(() => {
    if (!isFifaRevampEnabled) {
      return;
    }

    if (!isRouteAllowed(location.pathname, dashboardUser.roles)) {
      navigate(ROUTES.CREATOR_CRM, {replace: true});
    }
  }, [isFifaRevampEnabled, location.pathname, navigate, dashboardUser.roles]);
}

export default useFifaRevampRedirect;
